import { Stack, Row, Text, Panel } from '../ui';
import { useAccountingAr, useAccountingAp, useAccountingSummary } from '../api/hooks';
import { PaymentsTable } from './PaymentsTable';
import clsx from 'clsx';

function KvPanel({ label, value, currency, tone }: { label: string; value: number; currency?: string; tone?: 'positive'|'negative'|'neutral' }) {
  return (
    <div className="hairline rounded-sm p-4 flex-1 min-w-0">
      <Text variant="micro" muted>{label}</Text>
      <div className={clsx('text-xl sm:text-2xl font-semibold tnum mt-1',
        tone === 'positive' && 'text-positive',
        tone === 'negative' && 'text-negative',
        !tone && 'text-ink')}>
        {currency && <span className="text-sm font-normal text-muted mr-1">{currency}</span>}
        {Math.abs(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </div>
    </div>
  );
}

export function Accounting() {
  const { data: ar, loading: arLoading, refresh: refreshAr } = useAccountingAr();
  const { data: ap, loading: apLoading, refresh: refreshAp } = useAccountingAp();
  const { data: summary } = useAccountingSummary();

  const arOutstanding = summary ? (summary.ar.expected + summary.ar.logged) : 0;
  const apOutstanding = summary ? (summary.ap.expected + summary.ap.logged) : 0;
  const net = summary ? summary.net : 0;

  function handleRefresh() { refreshAr(); refreshAp(); }

  return (
    <Stack gap={4}>
      <div>
        <Text variant="display">Accounting</Text>
        <Text variant="small" muted>AR / AP tracking across all deals</Text>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KvPanel label="Total AR Outstanding" value={arOutstanding} currency="USD" tone="positive" />
        <KvPanel label="Total AP Outstanding" value={apOutstanding} currency="USD" tone="negative" />
        <KvPanel label="Net Position" value={net} currency="USD" tone={net >= 0 ? 'positive' : 'negative'} />
      </div>

      {summary?.currency_breakdown && Object.keys(summary.currency_breakdown).length > 1 && (
        <Panel title="By Currency">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(summary.currency_breakdown).map(([cur, vals]: [string, any]) => (
              <div key={cur} className="hairline rounded-sm p-3">
                <Text variant="micro" muted className="mb-1">{cur}</Text>
                <Stack gap={0.5}>
                  <Row justify="between"><Text variant="label">AR</Text><span className="tnum text-xs text-positive">{vals.ar.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></Row>
                  <Row justify="between"><Text variant="label">AP</Text><span className="tnum text-xs text-negative">{vals.ap.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></Row>
                  <Row justify="between"><Text variant="label">Net</Text><span className={clsx('tnum text-xs font-medium', vals.net >= 0 ? 'text-positive' : 'text-negative')}>{vals.net.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></Row>
                </Stack>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title={`Receivable (AR) - ${ar.length} payment${ar.length !== 1 ? 's' : ''}`}>
          {arLoading ? <Text variant="small" muted>loading...</Text> : <PaymentsTable payments={ar} onRefresh={handleRefresh} />}
        </Panel>
        <Panel title={`Payable (AP) - ${ap.length} payment${ap.length !== 1 ? 's' : ''}`}>
          {apLoading ? <Text variant="small" muted>loading...</Text> : <PaymentsTable payments={ap} onRefresh={handleRefresh} />}
        </Panel>
      </div>
    </Stack>
  );
}
